import type { EditorValue } from '@octopus-do/octopus-editor'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { type RefObject, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import { reportClientEvent } from '@/services/clientEvents'
import {
  countInputEvent,
  createInputActivity,
  describeTextLoss,
  detectEditorExtensions,
  INPUT_EVENTS,
  type InputActivity,
  measureTextLoss,
  TEXT_LOSS_THRESHOLD,
} from '../utils/textLoss'

// Every input event on its way to the editor is counted at the wrapper in the
// capture phase, so a handler that swallows it further down still shows up.
const trackInputActivity = (
  node: HTMLElement,
  activity: { current: InputActivity }
): (() => void) => {
  const count = (event: Event) => {
    activity.current = countInputEvent(activity.current, event)
  }

  INPUT_EVENTS.forEach((name) => {
    node.addEventListener(name, count, { capture: true })
  })

  return () => {
    INPUT_EVENTS.forEach((name) => {
      node.removeEventListener(name, count, { capture: true })
    })
  }
}

// Typed text the editor shows but its value does not carry (OCT-5217): an
// extension or input method that bypasses the Slate model. One report per
// mounted editor is enough; it carries the lost length, the writing
// assistants found in the page and a snapshot of what else is in it.
export const useTextLossReport = (wrapper: RefObject<HTMLElement | null>) => {
  const reported = useRef(false)
  const activity = useRef<InputActivity>(createInputActivity())
  const projectId = useSelector(migrationProjectSelector.getProjectId)

  useEffect(() => {
    const node = wrapper.current

    return node ? trackInputActivity(node, activity) : undefined
  }, [wrapper])

  return (value: EditorValue) => {
    if (reported.current) {
      return
    }

    const editable = wrapper.current?.querySelector('[contenteditable="true"]')

    if (!editable) {
      return
    }

    const lost = measureTextLoss(editable, value)

    if (lost < TEXT_LOSS_THRESHOLD) {
      return
    }

    reported.current = true

    reportClientEvent({
      type: 'editor_text_lost',
      reason: detectEditorExtensions(),
      details: describeTextLoss(editable, activity.current),
      value: lost,
      projectId,
    })
  }
}
